import "bootstrap/dist/css/bootstrap.min.css";
import "./MapNavbar.css";
import { assets } from "../../../publics/assets";
import PersonIcon from "@mui/icons-material/Person";
import LogoutIcon from "@mui/icons-material/Logout";
import HistoryToggleOffIcon from "@mui/icons-material/HistoryToggleOff";
import { Dropdown } from "react-bootstrap";
import { useContext, useEffect, useRef, useState } from "react";
import MenuIcon from "@mui/icons-material/Menu";
import { LightModeContext } from "../../../hooks/mode/LightModeContextProvider";
import { useAccessRights } from "../../../hooks/userPermissions/useAccessRights";
import Tooltip from "@mui/material/Tooltip";
import { useTranslation } from "react-i18next";
import { LANGUAGES } from "../../../constants/defaults";



function MapNavbar({
  handleDraggableComponentToggle,
  handleStyleChange,
  selectedStyle,
  mapRef,
  onToggleSidebar,
  handleMeteoComponentToggle,
  onShowUserComponent,
  showUserComponent,
  onToggleHistoricSidebar,
  showHistoricFiresList,
  onShowHistoricFiresList,
  onShowNavbarMenu,
  isSidebarPropagationOpen,
  isOpenHistoricSidebar,
  showLayersComponent,
  showMeteoComponent,
  toggleMapStylesSidebar,
  showMapStylesSidebar,
  handleLegendComponentToggle,
  showLegendComponent,
  geocoderContainerRefNavbar,
  showActiveFiresPanel,
  onToggleActiveFiresPanel,
  logout,
  onShowUserHistoricSection,
  onShowUserProfileSection,
}) {
  const { isLightMode } = useContext(LightModeContext);
  const { isServiceEnabled } = useAccessRights();
  const { t, i18n } = useTranslation();

  const [langOpen, setLangOpen] = useState(false);
  const langRef = useRef(null);

  const currentLang =
    LANGUAGES.find(
      (l) =>
        l.code === (i18n.resolvedLanguage || i18n.language || "fr").slice(0, 2),
    ) || LANGUAGES[0];

  const changeLanguage = (code) => {
    i18n.changeLanguage(code);
    setLangOpen(false);
  };

  // Close the language dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (langRef.current && !langRef.current.contains(e.target)) {
        setLangOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const isHistoricEnabled = isServiceEnabled("historic_fire_service");
  const isLayersEnabled = isServiceEnabled("display_layers_service");
  const isWeatherEnabled = isServiceEnabled("weather");
  const isEventCreationEnabled = isServiceEnabled("physical_model");
  // const isActiveFiresEnabled = isServiceEnabled("active_fires");
  const isActiveFiresEnabled = true;

  const styles = [
    {
      value: "mapbox://styles/mapbox/satellite-streets-v11",
      label: t("navbar.mapStyles.satellite"),
      slug: "satellite",
    },
    {
      value: "mapbox://styles/mapbox/streets-v11",
      label: t("navbar.mapStyles.streets"),
      slug: "routiere",
    },
    {
      value: "mapbox://styles/mapbox/outdoors-v11",
      label: t("navbar.mapStyles.outdoors"),
      slug: "outdoors",
    },
    {
      value: "mapbox://styles/mapbox/light-v10",
      label: t("navbar.mapStyles.light"),
      slug: "clair",
    },
  ];

  const openHistoricSidebar = () => {
    if (!isHistoricEnabled) return;
    onShowHistoricFiresList();
    onToggleHistoricSidebar();
  };

  const onOpenMenu = () => {
    if (isSidebarPropagationOpen) onToggleSidebar(true);
    if (isOpenHistoricSidebar) onToggleHistoricSidebar(true);
    if (showLayersComponent) handleDraggableComponentToggle(true);
    if (showMeteoComponent) handleMeteoComponentToggle(true);
    if (showMapStylesSidebar) toggleMapStylesSidebar(true);
    if (showLegendComponent) handleLegendComponentToggle(true);
    if (showUserComponent) onShowUserComponent(true);
    if (showActiveFiresPanel) onToggleActiveFiresPanel(true);
    onShowNavbarMenu();
  };

  return (
    <div className="nav-custom">
      <div className="nav-custom-container">
        <div className="map-logo-container">
          <img
            src={isLightMode ? assets.ActionLogoGray : assets.ActionLogo}
            alt="logo"
            className="map-logo"
          />
        </div>
        <div className="map-bar-class">
          <div className="search-bar" data-testid="navbar-search-input">
            <div
              ref={geocoderContainerRefNavbar}
              className="geocoder-container"
            ></div>
          </div>
          <div className="couches-bar">
            <li
              onClick={openHistoricSidebar}
              style={{
                cursor: isHistoricEnabled ? "pointer" : "not-allowed",
                opacity: isHistoricEnabled ? 1 : 0.5,
              }}
              data-testid="navbar-historical-fires-button"
            >
              {t("navbar.historicalFires")}
            </li>

            <li
              onClick={
                isLayersEnabled ? handleDraggableComponentToggle : undefined
              }
              style={{
                cursor: isLayersEnabled ? "pointer" : "not-allowed",
                opacity: isLayersEnabled ? 1 : 0.5,
              }}
              data-testid="navbar-layers-button"
            >
              {t("navbar.layers")}
            </li>
            <Dropdown
              style={{ paddingBottom: "8px" }}
              data-testid="navbar-map-style-button"
            >
              <Dropdown.Toggle id="dropdown-basic" className="drop">
                {t("navbar.baseMap")}
              </Dropdown.Toggle>
              <Dropdown.Menu
                className="dropdown-basemaps-menu"
                data-testid="navbar-map-style-menu"
              >
                {styles.map((style) => (
                  <div
                    key={style.value}
                    className="dropdown-items"
                    data-testid={`navbar-map-style-option-${style.slug}`}
                  >
                    <input
                      type="radio"
                      id={style.value}
                      name="mapStyle"
                      value={style.value}
                      onChange={() => handleStyleChange(style.value)}
                      checked={selectedStyle === style.value}
                      data-testid={`navbar-map-style-radio-${style.slug}`}
                    />
                    <label htmlFor={style.value} className="dropdown-labels">
                      {style.label}
                    </label>
                  </div>
                ))}
              </Dropdown.Menu>
            </Dropdown>
            <li
              onClick={
                isWeatherEnabled ? handleMeteoComponentToggle : undefined
              }
              style={{
                cursor: isWeatherEnabled ? "pointer" : "not-allowed",
                opacity: isWeatherEnabled ? 1 : 0.5,
              }}
              data-testid="navbar-weather-button"
            >
              {t("navbar.weather")}
            </li>
            <li
              onClick={
                isActiveFiresEnabled ? onToggleActiveFiresPanel : undefined
              }
              style={{
                cursor: isActiveFiresEnabled ? "pointer" : "not-allowed",
                opacity: isActiveFiresEnabled ? 1 : 0.5,
              }}
              data-testid="navbar-active-fire-button"
            >
              {t("navbar.activeFires")}
            </li>
            <li
              onClick={isEventCreationEnabled ? onToggleSidebar : undefined}
              style={{
                cursor: isEventCreationEnabled ? "pointer" : "not-allowed",
                opacity: isEventCreationEnabled ? 1 : 0.5,
                color: isEventCreationEnabled ? "red" : "#999",
              }}
              data-testid="navbar-create-event-button"
            >
              {t("navbar.createEvent")}
            </li>
          </div>

          <div className="map-icons-nav" data-testid="navbar-user-menu-button">
            <Tooltip title={t("navbar.profile")} arrow>
              <PersonIcon
                className="profile-icon"
                style={{ fontSize: "24px", cursor: "pointer" }}
                onClick={onShowUserProfileSection}
              />
            </Tooltip>
            <Tooltip title={t("navbar.eventsHistory")} arrow>
              <HistoryToggleOffIcon
                className="profile-icon"
                style={{ fontSize: "24px", cursor: "pointer" }}
                onClick={onShowUserHistoricSection}
              />
            </Tooltip>
            <Tooltip title={t("navbar.logout")} arrow>
              <LogoutIcon
                className="profile-icon"
                style={{ fontSize: "24px", cursor: "pointer" }}
                onClick={logout}
              />
            </Tooltip>

            <div
              className="lang-dropdown"
              ref={langRef}
              data-testid="navbar-language-toggle"
            >
              <button
                type="button"
                className={`lang-dropdown-toggle ${langOpen ? "open" : ""}`}
                onClick={() => setLangOpen((o) => !o)}
                aria-haspopup="listbox"
                aria-expanded={langOpen}
              >
                <img
                  src={currentLang.flag}
                  alt={currentLang.label}
                  className="lang-flag"
                />
                <span className="lang-label">{currentLang.label}</span>
                <svg
                  className="lang-chevron"
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </button>

              {langOpen && (
                <ul className="lang-dropdown-menu" role="listbox">
                  {LANGUAGES.map((lang) => (
                    <li
                      key={lang.code}
                      role="option"
                      aria-selected={lang.code === currentLang.code}
                      className={`lang-dropdown-item ${
                        lang.code === currentLang.code ? "selected" : ""
                      }`}
                      onClick={() => changeLanguage(lang.code)}
                      data-testid={`navbar-language-option-${lang.code}`}
                    >
                      <img
                        src={lang.flag}
                        alt={lang.label}
                        className="lang-flag"
                      />
                      <span>{lang.label}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
        <div
          className="menu-button"
          onClick={onOpenMenu}
          data-testid="navbar-menu-button"
        >
          <MenuIcon
            className="menu-icon"
            style={{ fontSize: "24px", cursor: "pointer" }}
          />
        </div>
      </div>
    </div>
  );
}

export default MapNavbar;